import {
  ACCOUNT_BATCH_SOURCE_TYPES,
  PLATFORM_SELECTIONS,
  SOURCE_PLATFORMS,
} from '@/entities/models'
import type {
  AccountBatch,
  AccountBatchChild,
  CreditAccount,
  CliDeviceAuthorization,
  CreatedDeveloperApiKey,
  DeveloperApiKey,
  DurationPackage,
  LedgerRecord,
  LedgerType,
  Order,
  PageResult,
  PlatformSelection,
  SourcePlatform,
  TaskQuote,
  TaskStatus,
  ApiKeyScope,
  TranscriptSegment,
  TranscriptTask,
} from '@/entities/models'
import type {
  AccountBatchRepository,
  CommerceRepository,
  CreditRepository,
  DeveloperRepository,
  OrderListQuery,
  TaskListQuery,
  TaskRepository,
} from '@/shared/api/contracts/repositories'
import { request } from '@/utils/http/client'
import type { RequestConfig } from '@/utils/http/types'
import { createUuid } from '@/shared/lib/uuid'

export interface ApiEnvelope<T> {
  code: string
  message: string
  data: T
  request_id: string
}

export type ApiTransport = <T>(config: RequestConfig) => Promise<T>

interface RawPage<T> {
  items: T[]
  page: number
  page_size: number
  total: number
}

interface RawTask {
  public_id: string
  source_platform: unknown
  title: string
  transcript_excerpt?: string
  status: TaskStatus
  stage?: TranscriptTask['stage']
  progress_percent: number
  duration_ms: number
  billable_seconds: number
  actual_duration_ms?: number | null
  charged_seconds?: number | null
  created_at: string
  updated_at: string
  terminal_at?: string | null
  canonical_url?: string
  transcript_text?: string | null
  segments?: Array<{
    sequence_no: number
    start_ms: number
    end_ms: number
    role_id?: string | null
    label?: string | null
    text: string
    confidence: number | null
  }>
  error_code?: string | null
  error_message?: string | null
  retry_of_task_public_id?: string | null
}

interface RawOrder {
  public_id: string
  order_no: string
  user_public_id?: string
  user_phone?: string
  package_code: string
  package_name: string
  duration_seconds: number
  original_price_cents: number
  payable_price_cents: number
  payment_channel: Order['channel']
  status: Order['status']
  expires_at: string
  paid_at: string | null
  canceled_at: string | null
  refunded_at: string | null
  credited_at: string | null
  credited?: boolean
  created_at: string
  updated_at: string
}

interface RawPackage {
  id?: string
  public_id?: string
  code: string
  name: string
  duration_seconds: number
  minutes?: number
  price_cents: number
  purchase_type: 'one_time'
  tag?: string | null
  enabled: boolean
  sort_order: number
  version?: number
}

interface RawDeveloperApiKey {
  public_id: string
  name: string
  display_prefix: string
  last_four: string
  scopes: ApiKeyScope[]
  status: 'active' | 'revoked'
  expires_at: string | null
  last_used_at: string | null
  created_at: string
  revoked_at: string | null
  api_key?: string
}

interface RawCliDeviceAuthorization {
  user_code: string
  client_name: string
  scopes: ApiKeyScope[]
  status: CliDeviceAuthorization['status']
  expires_at: string
}

interface RawAccountBatch {
  batch_id: string
  source_platform: unknown
  source_type: unknown
  max_items: number
  status: AccountBatch['status']
  collection_complete: boolean
  collection_stop_reason: AccountBatch['collectionStopReason']
  pause_reason: AccountBatch['pauseReason']
  has_more: boolean
  total: number
  discovered: number
  submitting: number
  queued: number
  processing: number
  paused: number
  completed: number
  failed: number
  items: Array<{
    sequence_no: number
    video_id: string
    task_id: string | null
    title: string
    status: AccountBatchChild['status']
    error_code: string | null
    error_message: string | null
  }>
}

const responseFormatError = () => new Error('服务响应格式无效')
const requestFormatError = () => new Error('请求参数格式无效')

const isAllowedValue = <Value extends string>(
  allowedValues: readonly Value[],
  value: unknown,
): value is Value =>
  typeof value === 'string' && allowedValues.some((allowedValue) => allowedValue === value)

const readSourcePlatform = (value: unknown): SourcePlatform => {
  if (!isAllowedValue(SOURCE_PLATFORMS, value)) {
    throw responseFormatError()
  }
  return value
}

const readPlatformSelection = (value: unknown): PlatformSelection => {
  if (!isAllowedValue(PLATFORM_SELECTIONS, value)) {
    throw requestFormatError()
  }
  return value
}

const readSourcePlatformInput = (value: unknown): SourcePlatform => {
  if (!isAllowedValue(SOURCE_PLATFORMS, value)) {
    throw requestFormatError()
  }
  return value
}

const readAccountBatchSourceType = (value: unknown): AccountBatch['sourceType'] => {
  if (!isAllowedValue(ACCOUNT_BATCH_SOURCE_TYPES, value)) {
    throw responseFormatError()
  }
  return value
}

const readAccountBatchSourceTypeInput = (value: unknown): AccountBatch['sourceType'] => {
  if (!isAllowedValue(ACCOUNT_BATCH_SOURCE_TYPES, value)) {
    throw requestFormatError()
  }
  return value
}

const unwrap = <T>(value: ApiEnvelope<T>): T => {
  if (!value || value.code !== 'OK' || !('data' in value)) {
    throw new Error('服务响应格式无效')
  }
  return value.data
}

const page = <T, U>(value: RawPage<T>, map: (item: T) => U): PageResult<U> => ({
  items: value.items.map(map),
  page: value.page,
  pageSize: value.page_size,
  total: value.total,
})

const timelineTime = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1_000)
  const hours = Math.floor(totalSeconds / 3_600)
  const minutes = Math.floor((totalSeconds % 3_600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':')
}

const mapTask = (value: RawTask): TranscriptTask => {
  const segments: TranscriptSegment[] = (value.segments ?? []).map((segment) => ({
    sequenceNo: segment.sequence_no,
    startMs: segment.start_ms,
    endMs: segment.end_ms,
    roleId: segment.role_id ?? '',
    label: segment.label ?? '',
    text: segment.text,
    confidence: segment.confidence,
  }))
  return {
    id: value.public_id,
    sourcePlatform: readSourcePlatform(value.source_platform),
    title: value.title,
    excerpt: value.transcript_excerpt ?? value.transcript_text?.slice(0, 240) ?? '',
    sourceUrl: value.canonical_url ?? '',
    status: value.status,
    stage: value.stage ?? (value.status === 'processing' ? 'transcribing' : value.status),
    progressPercent: value.progress_percent,
    createdAt: value.created_at,
    updatedAt: value.updated_at,
    convertedAt: value.terminal_at ?? null,
    durationMs: value.duration_ms,
    billableSeconds: value.billable_seconds,
    chargedSeconds: value.charged_seconds ?? 0,
    transcript: value.transcript_text ?? '',
    segments,
    timeline: segments.map((segment) => [timelineTime(segment.startMs), segment.label, segment.text]),
    error: value.error_message ?? undefined,
    errorCode: value.error_code ?? undefined,
    retryOfTaskId: value.retry_of_task_public_id,
  }
}

const mapOrder = (value: RawOrder): Order => ({
  id: value.public_id,
  orderNo: value.order_no,
  userId: value.user_public_id,
  username: value.user_phone,
  phone: value.user_phone,
  packCode: value.package_code,
  packName: value.package_name,
  minutes: Math.ceil(value.duration_seconds / 60),
  amountFen: value.payable_price_cents,
  channel: value.payment_channel,
  status: value.status,
  createdAt: value.created_at,
  updatedAt: value.updated_at,
  expiresAt: value.expires_at,
  paidAt: value.paid_at,
  canceledAt: value.canceled_at,
  credited: value.credited ?? value.credited_at !== null,
  refund: value.refunded_at
    ? { amountFen: value.payable_price_cents, completedAt: value.refunded_at }
    : null,
})

const mapPackage = (value: RawPackage): DurationPackage => ({
  id: value.id ?? value.public_id ?? '',
  code: value.code,
  name: value.name,
  minutes: value.minutes ?? Math.ceil(value.duration_seconds / 60),
  durationSeconds: value.duration_seconds,
  amountFen: value.price_cents,
  purchaseType: 'one-time',
  tag: typeof value.tag === 'string' ? value.tag : '',
  enabled: value.enabled,
  sortOrder: value.sort_order,
  version: value.version,
})

const mapDeveloperApiKey = (value: RawDeveloperApiKey): DeveloperApiKey => ({
  id: value.public_id,
  name: value.name,
  displayPrefix: value.display_prefix,
  lastFour: value.last_four,
  scopes: value.scopes,
  status: value.status,
  expiresAt: value.expires_at,
  lastUsedAt: value.last_used_at,
  createdAt: value.created_at,
  revokedAt: value.revoked_at,
})

const mapAccountBatch = (value: RawAccountBatch): AccountBatch => ({
  batchId: value.batch_id,
  sourcePlatform: readSourcePlatform(value.source_platform),
  sourceType: readAccountBatchSourceType(value.source_type),
  maxItems: value.max_items,
  status: value.status,
  collectionComplete: value.collection_complete,
  collectionStopReason: value.collection_stop_reason,
  pauseReason: value.pause_reason,
  hasMore: value.has_more,
  total: value.total,
  discovered: value.discovered,
  submitting: value.submitting,
  queued: value.queued,
  processing: value.processing,
  paused: value.paused,
  completed: value.completed,
  failed: value.failed,
  items: value.items.map((item) => ({
    sequenceNo: item.sequence_no,
    videoId: item.video_id,
    taskId: item.task_id,
    title: item.title,
    status: item.status,
    errorCode: item.error_code,
    errorMessage: item.error_message,
  })),
})

const ledgerLabels: Record<string, LedgerType> = {
  registration_bonus: '注册赠送',
  purchase: '购买',
  task_hold: '任务冻结',
  task_settlement: '结算',
  task_release: '失败退回',
  admin_adjustment: '后台调整',
  refund: '退款',
}

export function createHttpRepositories(transport: ApiTransport = request) {
  const accountBatches: AccountBatchRepository = {
    async create(input, idempotencyKey = createUuid()) {
      if (
        arguments.length < 1 || arguments.length > 2 ||
        typeof idempotencyKey !== 'string' ||
        !/^[A-Za-z0-9._:-]{1,128}$/.test(idempotencyKey) ||
        !input ||
        typeof input !== 'object' ||
        Array.isArray(input) ||
        typeof input.sourceUrl !== 'string' ||
        typeof input.maxItems !== 'number'
      ) {
        throw requestFormatError()
      }
      const sourcePlatform = readPlatformSelection(input.sourcePlatform)
      const sourceType = readAccountBatchSourceTypeInput(input.sourceType)
      return mapAccountBatch(
        unwrap(
          await transport<ApiEnvelope<RawAccountBatch>>({
            method: 'POST',
            url: '/account-batches',
            data: {
              source_platform: sourcePlatform,
              source_type: sourceType,
              source_url: input.sourceUrl,
              max_items: input.maxItems,
            },
            headers: { 'Idempotency-Key': idempotencyKey },
          }),
        ),
      )
    },
    async get(batchId) {
      return mapAccountBatch(
        unwrap(
          await transport<ApiEnvelope<RawAccountBatch>>({
            method: 'GET',
            url: `/account-batches/${encodeURIComponent(batchId)}`,
          }),
        ),
      )
    },
    async listActive(limit = 10) {
      return unwrap(
        await transport<ApiEnvelope<RawAccountBatch[]>>({
          method: 'GET',
          url: '/account-batches',
          params: { status: 'active', limit },
        }),
      ).map(mapAccountBatch)
    },
    async listRecent(query) {
      const rows = unwrap(await transport<ApiEnvelope<Array<RawAccountBatch & { created_at: string; source_url: string }>>>({
        method: 'GET',
        url: '/account-batches/recent',
        params: {
          source_platform: readSourcePlatformInput(query.sourcePlatform),
          source_type: readAccountBatchSourceTypeInput(query.sourceType),
          exclude_batch_id: query.excludeBatchId || undefined,
          limit: query.limit ?? 3,
        },
      }))
      return rows.map(row => ({ ...mapAccountBatch(row), createdAt: row.created_at, sourceUrl: row.source_url }))
    },
    async pause(batchId) {
      return mapAccountBatch(
        unwrap(
          await transport<ApiEnvelope<RawAccountBatch>>({
            method: 'POST',
            url: `/account-batches/${encodeURIComponent(batchId)}/pause`,
          }),
        ),
      )
    },
    async resume(batchId) {
      return mapAccountBatch(
        unwrap(
          await transport<ApiEnvelope<RawAccountBatch>>({
            method: 'POST',
            url: `/account-batches/${encodeURIComponent(batchId)}/resume`,
          }),
        ),
      )
    },
    async retryItem(batchId, sequenceNo, idempotencyKey) {
      return mapAccountBatch(
        unwrap(
          await transport<ApiEnvelope<RawAccountBatch>>({
            method: 'POST',
            url: `/account-batches/${encodeURIComponent(batchId)}/items/${sequenceNo}/retry`,
            headers: { 'Idempotency-Key': idempotencyKey },
          }),
        ),
      )
    },
  }

  const tasks: TaskRepository = {
    async quote(shareText, platform) {
      const data: { share_text: string; source_platform?: PlatformSelection } = {
        share_text: shareText,
      }
      if (platform !== undefined) data.source_platform = readPlatformSelection(platform)
      const raw = unwrap(
        await transport<
          ApiEnvelope<{
            quote_token: string
            source_platform: unknown
            title: string
            duration_ms: number
            billable_seconds: number
            available_seconds: number
            sufficient: boolean
            expires_at: string
          }>
        >({
          method: 'POST',
          url: '/task-quotes',
          timeout: 70_000,
          data,
        }),
      )
      return {
        quoteToken: raw.quote_token,
        sourcePlatform: readSourcePlatform(raw.source_platform),
        title: raw.title,
        durationMs: raw.duration_ms,
        billableSeconds: raw.billable_seconds,
        availableSeconds: raw.available_seconds,
        sufficient: raw.sufficient,
        expiresAt: raw.expires_at,
      } satisfies TaskQuote
    },
    async create(quoteToken, idempotencyKey) {
      return mapTask(
        unwrap(
          await transport<ApiEnvelope<RawTask>>({
            method: 'POST',
            url: '/tasks',
            data: { quote_token: quoteToken },
            headers: { 'Idempotency-Key': idempotencyKey },
          }),
        ),
      )
    },
    async list(query: TaskListQuery = {}) {
      const sourcePlatform =
        query.sourcePlatform === undefined
          ? undefined
          : readSourcePlatformInput(query.sourcePlatform)
      const sourceType =
        query.sourceType === undefined
          ? undefined
          : readAccountBatchSourceTypeInput(query.sourceType)
      const params = {
        search: query.search || undefined,
        status: query.status,
        source_platform: sourcePlatform,
        source_type: sourceType,
        date_from: query.dateFrom || undefined,
        date_to: query.dateTo || undefined,
        page: query.page ?? 1,
        page_size: query.pageSize ?? 10,
      }
      return page(
        unwrap(
          await transport<ApiEnvelope<RawPage<RawTask>>>({
            method: 'GET',
            url: '/tasks',
            params,
          }),
        ),
        mapTask,
      )
    },
    async get(id) {
      return mapTask(
        unwrap(await transport<ApiEnvelope<RawTask>>({ method: 'GET', url: `/tasks/${id}` })),
      )
    },
    async cancel(id) {
      return mapTask(
        unwrap(
          await transport<ApiEnvelope<RawTask>>({
            method: 'POST',
            url: `/tasks/${id}/cancel`,
          }),
        ),
      )
    },
    async retry(id, idempotencyKey) {
      return mapTask(
        unwrap(
          await transport<ApiEnvelope<RawTask>>({
            method: 'POST',
            url: `/tasks/${id}/retry`,
            headers: { 'Idempotency-Key': idempotencyKey },
          }),
        ),
      )
    },
    export(id, format) {
      return transport<Blob>({
        method: 'GET',
        url: `/tasks/${id}/export`,
        params: { format },
        responseType: 'blob',
      })
    },
  }

  const commerce: CommerceRepository = {
    async listPackages() {
      return unwrap(
        await transport<ApiEnvelope<RawPackage[]>>({ method: 'GET', url: '/packages' }),
      ).map(mapPackage)
    },
    async listOrders(query: OrderListQuery = {}) {
      return page(
        unwrap(
          await transport<ApiEnvelope<RawPage<RawOrder>>>({
            method: 'GET',
            url: '/orders',
            params: {
              status: query.status,
              date_from: query.dateFrom || undefined,
              date_to: query.dateTo || undefined,
              page: query.page ?? 1,
              page_size: query.pageSize ?? 10,
            },
          }),
        ),
        mapOrder,
      )
    },
    async getOrder(id) {
      return mapOrder(
        unwrap(await transport<ApiEnvelope<RawOrder>>({ method: 'GET', url: `/orders/${id}` })),
      )
    },
    async createOrder(packageId, channel, idempotencyKey) {
      return mapOrder(
        unwrap(
          await transport<ApiEnvelope<RawOrder>>({
            method: 'POST',
            url: '/orders',
            data: { package_id: packageId, payment_channel: channel },
            headers: { 'Idempotency-Key': idempotencyKey },
          }),
        ),
      )
    },
    async cancelOrder(id) {
      return mapOrder(
        unwrap(
          await transport<ApiEnvelope<RawOrder>>({
            method: 'POST',
            url: `/orders/${id}/cancel`,
          }),
        ),
      )
    },
    async repurchaseOrder(id, idempotencyKey) {
      return mapOrder(
        unwrap(
          await transport<ApiEnvelope<RawOrder>>({
            method: 'POST',
            url: `/orders/${id}/repurchase`,
            headers: { 'Idempotency-Key': idempotencyKey },
          }),
        ),
      )
    },
    async initiatePayment(orderId, channel, idempotencyKey) {
      const result = unwrap(
        await transport<ApiEnvelope<{ provider_meta?: { payment_url?: string } }>>({
          method: 'POST',
          url: `/orders/${orderId}/pay`,
          data: { payment_channel: channel },
          headers: { 'Idempotency-Key': idempotencyKey },
        }),
      )
      return { payment_url: result.provider_meta?.payment_url ?? '' }
    },
    async getPaymentStatus(orderId) {
      const result = unwrap(
        await transport<ApiEnvelope<{ status: string; credited: boolean }>>({
          method: 'GET',
          url: `/orders/${orderId}/payment-status`,
        }),
      )
      return result
    },
    async syncCasdoorPayments() {
      const result = unwrap(
        await transport<ApiEnvelope<{ synced: number; items: Array<Record<string, unknown>> }>>({
          method: 'POST',
          url: '/payment/casdoor/sync',
        }),
      )
      return result
    },
  }

  const credits: CreditRepository = {
    async getBalance() {
      const value = unwrap(
        await transport<
          ApiEnvelope<{
            available_seconds: number
            frozen_seconds: number
            consumed_seconds: number
            total_seconds: number
            updated_at: string
          }>
        >({ method: 'GET', url: '/credits/balance' }),
      )
      return {
        availableSeconds: value.available_seconds,
        frozenSeconds: value.frozen_seconds,
        consumedSeconds: value.consumed_seconds,
        totalSeconds: value.total_seconds,
        updatedAt: value.updated_at,
      } satisfies CreditAccount
    },
    async listLedger(options = {}) {
      const value = unwrap(
        await transport<
          ApiEnvelope<
            RawPage<{
              public_id: string
              entry_type: string
              reference_type: string
              reference_public_id: string
              delta_available: number
              delta_frozen: number
              delta_consumed: number
              created_at: string
            }>
          >
        >({
          method: 'GET',
          url: '/credits/ledger',
          params: {
            entry_type: options.entryType,
            date_from: options.dateFrom,
            date_to: options.dateTo,
            page: options.page ?? 1,
            page_size: options.pageSize ?? 10,
          },
        }),
      )
      return page(value, (item): LedgerRecord => ({
        id: item.public_id,
        type: ledgerLabels[item.entry_type] ?? '结算',
        entryType: item.entry_type,
        referenceType: item.reference_type,
        deltaSeconds:
          item.entry_type === 'task_settlement'
            ? -Math.abs(item.delta_consumed)
            : item.delta_available,
        createdAt: item.created_at,
        reference: item.reference_public_id,
      }))
    },
  }

  const developer: DeveloperRepository = {
    async createApiKey(input) {
      const value = unwrap(
        await transport<ApiEnvelope<RawDeveloperApiKey>>({
          method: 'POST',
          url: '/api-keys',
          data: {
            name: input.name,
            scopes: input.scopes,
            expires_at: input.expiresAt ?? null,
          },
        }),
      )
      if (!value.api_key) throw new Error('密钥创建响应缺少一次性明文')
      return { ...mapDeveloperApiKey(value), apiKey: value.api_key } satisfies CreatedDeveloperApiKey
    },
    async listApiKeys() {
      return unwrap(
        await transport<ApiEnvelope<RawDeveloperApiKey[]>>({
          method: 'GET',
          url: '/api-keys',
        }),
      ).map(mapDeveloperApiKey)
    },
    async revokeApiKey(id) {
      return mapDeveloperApiKey(
        unwrap(
          await transport<ApiEnvelope<RawDeveloperApiKey>>({
            method: 'DELETE',
            url: `/api-keys/${id}`,
          }),
        ),
      )
    },
    async getCliAuthorization(userCode) {
      const value = unwrap(
        await transport<ApiEnvelope<RawCliDeviceAuthorization>>({
          method: 'GET',
          url: `/cli/device/authorizations/${encodeURIComponent(userCode)}`,
        }),
      )
      return {
        userCode: value.user_code,
        clientName: value.client_name,
        scopes: value.scopes,
        status: value.status,
        expiresAt: value.expires_at,
      }
    },
    async approveCliAuthorization(userCode) {
      const value = unwrap(
        await transport<ApiEnvelope<{ user_code: string; status: 'approved' }>>({
          method: 'POST',
          url: `/cli/device/authorizations/${encodeURIComponent(userCode)}/approve`,
        }),
      )
      return { userCode: value.user_code, status: value.status }
    },
  }

  return { accountBatches, tasks, commerce, credits, developer }
}

export const repositories = createHttpRepositories()
